import React from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { TdInputProp } from './types';
import ValidationIndicator from './ValidationIndicator';

export const TdInput = ({
  label, name, placeholder, type, id, register, error, errorMessage, width, isIcon = false,
  value, handleInputOnChange, isValid = false, required = false, reserveErrorSpace = false,
}: TdInputProp) => {
  const isEmail = type === 'email';
  const showInvalid = !!error;
  const showValid = !showInvalid && isValid;
  const stateClass = showInvalid
    ? 'border-2 border-doqit-highlight-pink text-doqit-highlight-pink placeholder:text-doqit-highlight-pink'
    : 'border border-doqit-purple-150 text-doqit-deep-purple placeholder:text-doqit-mid-purple';
  const widthClass = width || 'w-full';
  const padLeft = isIcon ? 'pl-12' : 'pl-[21px]';
  const padRight = 'pr-[21px]';
  const baseInputClass = 'h-[71px] rounded-[12px] font-body font-medium text-[19px] leading-[19px] tracking-[0.5px] bg-doqit-purple-25 outline-none placeholder:opacity-70';
  const insetShadow = 'shadow-[inset_2px_2px_4px_rgba(57,38,79,0.2)]';
  const inputClass = `block ${widthClass} ${padLeft} ${padRight} ${baseInputClass} ${insetShadow} ${stateClass}`;

  return (
    <label htmlFor={id} className="relative block">
      {label && (
        <span
          className="block text-doqit-mid-purple text-[15px] leading-[19px] tracking-[0.5px] font-medium font-body mb-1.5 min-h-[38px]"
        >
          {label}
          {required && <span className="text-doqit-highlight-pink ml-0.5">*</span>}
        </span>
      )}
      <div className="relative">
        {isIcon && (
          <CalendarIcon className="pointer-events-none w-7 h-7 absolute left-3 top-1/2 -translate-y-1/2 text-doqit-mid-purple/70" />
        )}
        <input
          id={id}
          type={type}
          // An email box must never be handed to the keyboard as ordinary
          // prose. Without these, Android keyboards capitalise the first
          // letter and autocorrect can rewrite the domain, and because we
          // match addresses on the server that was enough to lose someone
          // their password reset with no error shown (869ep1rnj). iOS infers
          // this from type="email" on its own, which is why the bug only ever
          // showed up on Android.
          autoCapitalize={isEmail ? 'none' : undefined}
          autoCorrect={isEmail ? 'off' : undefined}
          spellCheck={isEmail ? false : undefined}
          autoComplete={isEmail ? 'email' : undefined}
          inputMode={isEmail ? 'email' : undefined}
          {...register(name)}
          placeholder={placeholder}
          className={inputClass}
          value={value}
          onChange={(e) => handleInputOnChange && handleInputOnChange(e.target.value)}
        />
        {showInvalid && (
          <ValidationIndicator state="invalid" className="absolute right-3 -top-[10px]" />
        )}
        {showValid && (
          <ValidationIndicator state="valid" className="absolute right-3 -top-[10px]" />
        )}
      </div>
      {showInvalid && errorMessage && (
        <p className={`${name}-error mt-1.5 text-doqit-highlight-pink text-[15px] leading-[19px] tracking-[0.5px] font-bold font-body`}>
          {errorMessage}
        </p>
      )}
      {/* Holds the error line's height while there is no error, so blur
          validation doesn't push everything below it down (opt-in). */}
      {reserveErrorSpace && !(showInvalid && errorMessage) && (
        <span aria-hidden="true" className="block mt-1.5 h-[19px]" />
      )}
    </label>
  );
};

export default TdInput;
